import React, { useEffect, useState } from 'react';
import { Alert, Box, CircularProgress, Link, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useToast } from '../../context/ToastContext';
import { AuthService } from '../../services/auth.service';
import { ExportFinanceService } from '../../services/exportFinance.service';
import { PcPosition } from '../../interfaces/exportFinance.interface';
import { formatMoney } from '../../utils/format';
import { formatDate } from '../../utils/dateUtils';

const Stat: React.FC<{ label: string; value: string; testId?: string }> = ({ label, value, testId }) => (
  <Box sx={{ flex: '1 1 160px', minWidth: 0 }}>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Typography data-testid={testId}>{value}</Typography>
  </Box>
);

/** Read-only packing-credit position of one export LC (W3-B8): eligibility, drawn, liquidated, headroom. */
const LcPackingCreditPanel: React.FC<{ lcId: string }> = ({ lcId }) => {
  const toast = useToast();
  const institutionId = AuthService.getSelectedInstitution() ?? '';
  const [pos, setPos] = useState<PcPosition | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!institutionId) return;
    let live = true;
    (async () => {
      try {
        setLoading(true);
        const p = await ExportFinanceService.pcPosition(institutionId, lcId);
        if (live) setPos(p);
      } catch (e) {
        toast.showApiError(e, 'Failed to load the packing-credit position');
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => { live = false; };
  }, [institutionId, lcId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <CircularProgress />;
  if (!pos) return null;
  return (
    <Paper sx={{ p: 2 }} data-testid="lc-pc-panel" data-help-section="commercial.packingCredit.position">
      <Typography variant="h6" sx={{ mb: 1 }} data-help="commercial.packingCredit.title">Packing credit</Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <Stat label={`LC amount (${pos.currencyCode})`} value={formatMoney(pos.lcAmount)} />
        <Stat label="Less back-to-back LCs" value={formatMoney(pos.b2bAmount)} />
        <Stat label="PC base" value={formatMoney(pos.pcBaseForeign)} />
        <Stat label="Eligibility %" value={pos.eligibilityPct != null ? `${pos.eligibilityPct}%` : '—'} />
        <Stat label="Eligibility (BDT)" value={formatMoney(pos.eligibilityBase, 'BDT')} testId="pc-eligibility" />
        <Stat label="Drawn" value={formatMoney(pos.drawn, 'BDT')} />
        <Stat label="Liquidated from proceeds" value={formatMoney(pos.liquidated, 'BDT')} />
        <Stat label="Outstanding" value={formatMoney(pos.outstanding, 'BDT')} testId="pc-outstanding" />
        <Stat label="Headroom" value={formatMoney(pos.headroom, 'BDT')} testId="pc-headroom" />
      </Box>
      {pos.warnings.map(w => <Alert key={w} severity="warning" sx={{ mb: 1 }}>{w}</Alert>)}
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell data-help="commercial.packingCredit.col.facility">Facility</TableCell><TableCell data-help="commercial.packingCredit.col.drawdown">Drawdown</TableCell><TableCell data-help="commercial.packingCredit.col.date">Date</TableCell>
              <TableCell align="right" data-help="commercial.packingCredit.col.amount">Amount</TableCell><TableCell align="right" data-help="commercial.packingCredit.col.outstanding">Outstanding</TableCell><TableCell data-help="commercial.packingCredit.col.status">Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pos.drawdowns.map(d => (
              <TableRow key={d.drawdownId}>
                <TableCell><Link component={RouterLink} to={`/financing/facilities/${d.facilityId}`}>{d.facilityNo}</Link></TableCell>
                <TableCell>{d.drawdownNo}</TableCell>
                <TableCell>{formatDate(d.drawdownDate)}</TableCell>
                <TableCell align="right">{formatMoney(d.amount, 'BDT')}</TableCell>
                <TableCell align="right">{formatMoney(d.outstanding, 'BDT')}</TableCell>
                <TableCell>{d.status}</TableCell>
              </TableRow>
            ))}
            {pos.drawdowns.length === 0 && <TableRow><TableCell colSpan={6} align="center">No packing credit drawn against this LC.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default LcPackingCreditPanel;
